import { useState, useEffect, useContext } from 'react';
import { Box, Typography, Stack, Textarea, Button, IconButton, Sheet, Avatar, Chip, Divider } from '@mui/joy';
import { ThumbsUp, Trash, ArrowBendUpLeft, ChatCircle, PaperPlaneRight } from '@phosphor-icons/react';
import { LayoutContext } from '../context';
import { APIGetQuestionDiscussions, APICreateDiscussion, APIDeleteDiscussion, APIToggleDiscussionLike } from '../api';

export default function DiscussionPanel({ questionId }) {
    const { token, user } = useContext(LayoutContext);
    const [discussions, setDiscussions] = useState([]);
    const [total, setTotal] = useState(0);
    const [newComment, setNewComment] = useState('');
    const [replyTo, setReplyTo] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (questionId && expanded) {
            loadDiscussions();
        }
    }, [questionId, expanded]);

    const loadDiscussions = async () => {
        try {
            const res = await APIGetQuestionDiscussions(token, questionId);
            if (res.success) {
                setDiscussions(res.discussions);
                setTotal(res.total);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleSubmit = async () => {
        if (!newComment.trim()) return;
        setSubmitting(true);
        try {
            const res = await APICreateDiscussion(token, {
                question_id: questionId,
                comment: newComment.trim()
            });
            if (res.success) {
                setNewComment('');
                loadDiscussions();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setSubmitting(false);
        }
    };

    const handleReply = async (parentId) => {
        if (!replyText.trim()) return;
        setSubmitting(true);
        try {
            const res = await APICreateDiscussion(token, {
                question_id: questionId,
                comment: replyText.trim(),
                parent_comment_id: parentId
            });
            if (res.success) {
                setReplyTo(null);
                setReplyText('');
                loadDiscussions();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setSubmitting(false);
        }
    };

    const handleLike = async (discussionId) => {
        try {
            const res = await APIToggleDiscussionLike(token, discussionId);
            if (res.success) {
                loadDiscussions();
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleDelete = async (discussionId) => {
        try {
            const res = await APIDeleteDiscussion(token, discussionId);
            if (res.success) {
                loadDiscussions();
            }
        } catch (e) {
            console.error(e);
        }
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        const now = new Date();
        const diffMs = now - d;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'только что';
        if (diffMins < 60) return `${diffMins} мин назад`;
        if (diffHours < 24) return `${diffHours} ч назад`;
        if (diffDays < 7) return `${diffDays} дн назад`;
        return d.toLocaleDateString('ru-RU');
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Button
                variant="plain"
                color="neutral"
                startDecorator={<ChatCircle size={18} />}
                onClick={() => setExpanded(!expanded)}
                sx={{ mb: 1 }}
            >
                Обсуждение {total > 0 && `(${total})`}
            </Button>

            {expanded && (
                <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'lg' }}>
                    {/* New comment input */}
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Textarea
                            placeholder="Написать комментарий..."
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            minRows={1}
                            maxRows={4}
                            sx={{ flex: 1 }}
                        />
                        <IconButton
                            variant="solid"
                            color="primary"
                            onClick={handleSubmit}
                            loading={submitting}
                            disabled={!newComment.trim()}
                        >
                            <PaperPlaneRight size={18} />
                        </IconButton>
                    </Stack>

                    {/* Discussion list */}
                    <Stack spacing={1.5}>
                        {discussions.length === 0 && (
                            <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center', py: 2 }}>
                                Пока нет комментариев. Будьте первым!
                            </Typography>
                        )}

                        {discussions.map((d) => (
                            <Box key={d.id}>
                                <CommentItem
                                    comment={d}
                                    user={user}
                                    onLike={handleLike}
                                    onDelete={handleDelete}
                                    onReply={() => setReplyTo(replyTo === d.id ? null : d.id)}
                                    formatDate={formatDate}
                                />

                                {/* Replies */}
                                {d.replies && d.replies.length > 0 && (
                                    <Stack spacing={1} sx={{ ml: 4, mt: 1 }}>
                                        {d.replies.map((r) => (
                                            <CommentItem
                                                key={r.id}
                                                comment={r}
                                                user={user}
                                                onLike={handleLike}
                                                onDelete={handleDelete}
                                                formatDate={formatDate}
                                                isReply
                                            />
                                        ))}
                                    </Stack>
                                )}

                                {/* Reply input */}
                                {replyTo === d.id && (
                                    <Stack direction="row" spacing={1} sx={{ ml: 4, mt: 1 }}>
                                        <Textarea
                                            placeholder="Ответить..."
                                            value={replyText}
                                            onChange={(e) => setReplyText(e.target.value)}
                                            minRows={1}
                                            maxRows={3}
                                            sx={{ flex: 1 }}
                                            size="sm"
                                        />
                                        <IconButton
                                            variant="soft"
                                            color="primary"
                                            size="sm"
                                            onClick={() => handleReply(d.id)}
                                            loading={submitting}
                                            disabled={!replyText.trim()}
                                        >
                                            <PaperPlaneRight size={18} />
                                        </IconButton>
                                    </Stack>
                                )}
                            </Box>
                        ))}
                    </Stack>
                </Sheet>
            )}
        </Box>
    );
}

function CommentItem({ comment, user, onLike, onDelete, onReply, formatDate, isReply = false }) {
    const isOwner = user?.id === comment.user;
    const isAdmin = user?.role === 'admin';

    return (
        <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'md' }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <Avatar size="sm" src={comment.user_avatar}>
                    {comment.user_name?.[0]?.toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography level="body-sm" fontWeight="bold">
                            {comment.user_name}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                            {formatDate(comment.date_create)}
                        </Typography>
                    </Stack>
                    <Typography level="body-sm" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                        {comment.comment}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Button
                            variant="plain"
                            color={comment.is_liked ? 'primary' : 'neutral'}
                            size="sm"
                            startDecorator={comment.is_liked ? <ThumbsUp size={16} weight="fill" /> : <ThumbsUp size={16} />}
                            onClick={() => onLike(comment.id)}
                            sx={{ minHeight: 'auto', py: 0, px: 0.5 }}
                        >
                            {comment.likes_count > 0 && comment.likes_count}
                        </Button>
                        {!isReply && onReply && (
                            <Button
                                variant="plain"
                                color="neutral"
                                size="sm"
                                startDecorator={<ArrowBendUpLeft size={16} />}
                                onClick={onReply}
                                sx={{ minHeight: 'auto', py: 0, px: 0.5 }}
                            >
                                Ответить
                            </Button>
                        )}
                        {(isOwner || isAdmin) && (
                            <IconButton
                                variant="plain"
                                color="danger"
                                size="sm"
                                onClick={() => onDelete(comment.id)}
                                sx={{ ml: 'auto' }}
                            >
                                <Trash size={16} />
                            </IconButton>
                        )}
                    </Stack>
                </Box>
            </Stack>
        </Sheet>
    );
}
